/**
 * EdgeViewCycler — Hidden edge-tap camera view cycling
 *
 * Invisible tap zones on the left and right screen edges let the user
 * cycle through all namedViews defined in model config. No persistent UI —
 * the feature is "discovered" by tapping the edge.
 *
 * On tap:
 *   1. Engine animates to the next / previous namedView
 *   2. A large white ripple bursts from the edge (clipped by overflow-hidden)
 *   3. A prev/next chevron icon briefly fades in and out
 *
 * Tap zones are inset from the top (80 px — clears the top bar) and bottom
 * (80 px — clears the menu bar) so they don't interfere with other controls.
 *
 * Only active when:
 *   - engine is mounted (engineRef.current != null)
 *   - sceneReady (passed as `enabled`)
 *   - cameraMode === 'exterior' (pano view has its own gaze controls)
 *   - at least 2 namedViews are configured for the model
 *
 * @see packages/brand-audi/src/components/screens/Viewer.tsx
 */
import { useRef, useState, useCallback, useEffect } from 'react';
import type { Engine } from '@ims/engine';
import type { NamedView } from '@ims/shared';

// ─── Style injection (once per session) ───────────────────────────────────────
let _stylesInjected = false;
const CSS = `
@keyframes _edgeRipple {
  0%   { transform: scale(0.06); opacity: 0.55; }
  100% { transform: scale(1);    opacity: 0;    }
}
`;

function injectStyles() {
  if (_stylesInjected) return;
  _stylesInjected = true;
  const tag = document.createElement('style');
  tag.textContent = CSS;
  document.head.appendChild(tag);
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface RippleState {
  id: number;
  side: 'left' | 'right';
}

export interface EdgeViewCyclerProps {
  engineRef: React.RefObject<Engine | null>;
  namedViews: Record<string, NamedView> | undefined;
  enabled: boolean;
}

// ─── Icon paths ───────────────────────────────────────────────────────────────
const ICON_PREV = '/app/icons/prev-btn.svg';
const ICON_NEXT = '/app/icons/next-btn.svg';

// ─── Component ────────────────────────────────────────────────────────────────

export function EdgeViewCycler({ engineRef, namedViews, enabled }: EdgeViewCyclerProps) {
  useEffect(injectStyles, []);

  const viewKeys  = Object.keys(namedViews ?? {});
  const indexRef  = useRef(0);
  const rippleId  = useRef(0);
  const [ripple, setRipple] = useState<RippleState | null>(null);
  const [iconVisible, setIconVisible] = useState(false);

  const go = useCallback(
    (direction: 'prev' | 'next') => {
      const engine = engineRef.current;
      if (!engine || viewKeys.length < 2) return;

      indexRef.current =
        direction === 'next'
          ? (indexRef.current + 1) % viewKeys.length
          : (indexRef.current - 1 + viewKeys.length) % viewKeys.length;

      const view = namedViews![viewKeys[indexRef.current]];
      engine.setViewpoint({
        position:           view.position,
        target:             view.target,
        transitionDuration: view.transitionDuration ?? 800,
      });

      const id   = ++rippleId.current;
      const side = direction === 'next' ? 'right' : 'left';
      setRipple({ id, side });
      setIconVisible(true);
      // Fade icon out before ripple ends
      setTimeout(() => setIconVisible(false), 550);
      // Remove ripple element when animation completes
      setTimeout(() => setRipple((cur) => (cur?.id === id ? null : cur)), 850);
    },
    [engineRef, namedViews, viewKeys],
  );

  // Don't render anything if there aren't enough views or scene isn't ready
  if (!enabled || viewKeys.length < 2) return null;

  return (
    <>
      {/* ── Left tap zone — prev view ──────────────────────────────────── */}
      <div
        className="absolute left-0 top-70 bottom-70 w-[12%] z-10"
        onClick={() => go('prev')}
        aria-hidden="true"
      />

      {/* ── Right tap zone — next view ─────────────────────────────────── */}
      <div
        className="absolute right-0 top-70 bottom-70 w-[12%] z-10"
        onClick={() => go('next')}
        aria-hidden="true"
      />

      {/* ── Ripple + icon feedback ─────────────────────────────────────── */}
      {ripple && (
        <div key={ripple.id} className="pointer-events-none absolute inset-0 z-10">

          {/* Large white circle — flex-centered vertically so no translateY
              conflicts with the scale animation */}
          <div
            className={[
              'absolute inset-y-0 flex items-center',
              ripple.side === 'left' ? 'left-0 -ml-120' : 'right-0 -mr-120',
            ].join(' ')}
          >
            <div
              className="w-150 h-150 rounded-full bg-white shrink-0"
              style={{
                animation: '_edgeRipple 0.85s cubic-bezier(0.2, 0.8, 0.4, 1) forwards',
              }}
            />
          </div>

          {/* Prev / Next icon — opacity driven by React state + transition, no keyframes */}
          <div
            className={[
              'absolute inset-y-0 flex items-center',
              ripple.side === 'left' ? 'left-2' : 'right-2',
            ].join(' ')}
          >
            <img
              src={ripple.side === 'left' ? ICON_PREV : ICON_NEXT}
              alt=""
              className="w-9 h-9"
              style={{
                opacity: iconVisible ? 0.7 : 0,
                transition: 'opacity 0.18s ease',
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
